import { useQuery } from "@tanstack/react-query";

import { digitalHumanService, type DhVideoLimitsDto } from "../api/digitalHumanService";
import { MAX_AVATAR_VIDEO_SECONDS } from "@/entities/dh-profile/model/dhUploadLimits";
import { VIDEO_SCRIPT_MAX } from "./dhCreateOptions";

/**
 * 视频档(真人视频换口播)时长上限 —— 1020 起**按出片通道取值**(新通道放开到 5 分钟,老通道仍 2 分钟)。
 *
 * ★单一来源在服务端(ai 配置 → api `GET /digital-human/video-limits`):文案字数上限、上传时长闸、
 *   页面上「最长 X」的文案都读这一份,别再在页面里写死 540 / 120 / 2 分钟。
 * ★拿不到(接口挂/老后端)回落老口径 540 字 / 120 秒:宁可少放开,也不让用户写了长稿再被后端拒。
 */
export interface DhVideoLimits {
  /** 源视频下限(秒,0 = 不限)。 */
  minSourceSec: number;
  /** 源视频上限(秒)。 */
  maxSourceSec: number;
  /** 成片/文案上限(秒)。 */
  maxScriptSec: number;
  /** 文案字数上限(= maxScriptSec × 4.5)。 */
  maxScriptChars: number;
}

export const DEFAULT_DH_VIDEO_LIMITS: DhVideoLimits = {
  minSourceSec: 0,
  maxSourceSec: MAX_AVATAR_VIDEO_SECONDS,
  maxScriptSec: MAX_AVATAR_VIDEO_SECONDS,
  maxScriptChars: VIDEO_SCRIPT_MAX,
};

const pos = (v: unknown): number =>
  typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;

/** 回包 → 上限;字段缺/非正 → 回落默认(逐字段不混拼:要么整份可信,要么整份回落)。 */
export function normalizeDhVideoLimits(dto: DhVideoLimitsDto | null | undefined): DhVideoLimits {
  if (!dto) return DEFAULT_DH_VIDEO_LIMITS;
  const maxSourceSec = pos(dto.maxSourceSec);
  const maxScriptSec = pos(dto.maxScriptSec);
  const maxScriptChars = pos(dto.maxScriptChars);
  if (!maxSourceSec || !maxScriptSec || !maxScriptChars) return DEFAULT_DH_VIDEO_LIMITS;
  return { minSourceSec: pos(dto.minSourceSec), maxSourceSec, maxScriptSec, maxScriptChars };
}

/** 「120 秒」/「5 分钟」:120 及以下按秒(与改造前文案逐字一致),整分钟长片按分钟。 */
export function dhDurationLabel(sec: number): string {
  return sec > 120 && sec % 60 === 0 ? `${sec / 60} 分钟` : `${sec} 秒`;
}

/** 源视频时长文案:整分钟按分钟(「2 分钟」/「5 分钟」,与上传闸报错同口径),否则按秒。 */
export function dhSourceDurationLabel(sec: number): string {
  return sec >= 60 && sec % 60 === 0 ? `${sec / 60} 分钟` : `${sec} 秒`;
}

/** 部署期配置,会话内不变 —— 与 price-variants 同样缓存久一点。 */
export function useDhVideoLimits(): DhVideoLimits {
  const q = useQuery({
    queryKey: ["dh", "video-limits"],
    queryFn: () => digitalHumanService.getVideoLimits(),
    staleTime: 10 * 60_000,
    retry: 1,
  });
  return normalizeDhVideoLimits(q.data);
}
